import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button, Box } from '@mui/material';
import MultipleChipSelect from '../../../Components/Inputs/MultipleChipSelect';
import CheckboxStack from '../../../Components/Inputs/CheckboxStack';
import { CHANNELS } from '../Utils/constants';

//outcomes that can't be selected together (BE filters are in AND)
const EXCLUSIVE_OUTCOMES = {
  delivered: ['notDelivered'],
  opened: ['notDelivered'],
  notDelivered: ['delivered', 'opened'],
};

const useStyles = () => ({
  root: {
    padding: '8px 16px',
    width: '25vw',
  },
});

//tags are identified by id (like in the CTA editor): name can be missing or duplicated
const mapOptions = (tags = []) => {
  return [
    { value: 'delivered', label: 'DELIVERED' },
    { value: 'opened', label: 'OPENED' },
    { value: 'notDelivered', label: 'NOT_DELIVERED' },
    ...tags.map((tag) => ({
      tagId: tag?.id,
      value: tag?.id,
      label: tag?.name || tag?.id,
    })),
  ];
};

const FiltersWidget = function FiltersWidget({
  onClose = () => {},
  onConfirm = () => {},
  tags = [],
  defaultFilters = {},
}) {
  const intl = useIntl();
  const classes = useStyles();
  const [outcomes, setOutcomes] = useState(null);
  const [channel, setChannel] = useState(null);

  useEffect(() => {
    if (defaultFilters) {
      const defaultOutcomes = [...(defaultFilters?.tagId || [])];
      if (defaultFilters?.sent !== null && defaultFilters?.sent !== undefined)
        defaultOutcomes.push(
          defaultFilters?.sent ? 'delivered' : 'notDelivered'
        );
      if (defaultFilters?.opened) defaultOutcomes.push('opened');

      setOutcomes(defaultOutcomes);
      if (defaultFilters?.activityType) {
        const newChannel = CHANNELS.map(({ value, activityType }) =>
          defaultFilters.activityType.includes(activityType) ? value : null
        );
        setChannel(newChannel);
      }
    }
  }, [defaultFilters]);

  const handleOutcomesChange = (newValues) => {
    if (!Array.isArray(newValues) || newValues.length === 0) setOutcomes(null);
    else {
      //the last selected outcome wins over the ones it conflicts with
      const added = newValues.find((value) => !outcomes?.includes(value));
      setOutcomes(
        newValues.filter((value) => !EXCLUSIVE_OUTCOMES[added]?.includes(value))
      );
    }
  };

  const handleChangeChannel = (newValues) => {
    setChannel(newValues);
  };

  const handleConfirm = () => {
    let filtersValue = {};
    const tagId = [];

    if (outcomes) {
      let opened = null;
      let sent = null;
      outcomes.forEach((o) => {
        const selectedOption = mapOptions(tags).find(
          ({ value }) => value === o
        );
        if (selectedOption?.tagId) tagId.push(selectedOption?.tagId);
        else {
          if (selectedOption?.value === 'opened') opened = true;
          if (selectedOption?.value === 'delivered') sent = true;
          if (selectedOption?.value === 'notDelivered') sent = false;
        }
      });
      filtersValue = { ...filtersValue, opened, sent };
    } else {
      filtersValue = { opened: null, sent: null };
    }

    const activityType = channel
      ?.filter((value) => value !== null)
      ?.map((value) => CHANNELS[parseInt(value, 10)]?.activityType);

    onConfirm({
      ...{ opened: filtersValue?.opened, sent: filtersValue?.sent },
      activityType,
      tagId: tagId.length > 0 ? tagId : null,
    });
    onClose();
  };
  return (
    <div style={classes.root}>
      <CheckboxStack
        direction="row"
        data={CHANNELS.map(({ label, value }) => ({
          label: intl.messages[label],
          value,
        }))}
        label={intl.messages.CHANNEL}
        handleChange={handleChangeChannel}
        defaultCheckedAr={channel}
        noEmptyValues
      />
      <MultipleChipSelect
        label={intl.messages.OUTCOME}
        onChange={handleOutcomesChange}
        values={outcomes || []}
        options={mapOptions(tags)}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <Button
          id="confirm-filters-btn"
          size="large"
          sx={{ mt: '16px' }}
          onClick={handleConfirm}
        >
          {intl.messages.CONFIRM}
        </Button>
      </Box>
    </div>
  );
};

FiltersWidget.propTypes = {
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  tags: PropTypes.array,
  defaultFilters: PropTypes.object,
};

export default FiltersWidget;
